<template>
  <div class="note-metadata-wrapper">
    <v-popover
      v-if="showMetadata.attendees"
      ref="attendeesPopover"
      interactive
      tag="div"
      content-tag="div"
      :trigger="'manual'"
      :role="'poper'"
      :placement="'bottom-start'"
      :on-hide="handleUpdateAttendees"
      :on-click-outside="(i, e) => handleAttendeesClickOutside(i, e)"
      :hide-on-click="false"
      @click="handleClickAttendeesPopover"
    >
      <CurrentModelMembers
        class="pt add-members-wrapper"
        :avatar-size="'w-5 h-5 text-sm'"
        :members="attendees"
        :always-allow-click="true"
        :tooltip-text="translate('editAttendees')"
        @clickWrap="openMetadataDropdown('attendees')"
      />
      <template #content="{ state, hide }">
        <MembersDropdownList
          v-if="state.isVisible"
          ref="attendeesDropdown"
          :entity-type-prop="constants.meetingAttendees"
          :members="members"
          @memberClick="handleAttendeeClick"
          @onMoreOptionsHide="focusAttendeesDropdown"
          @closeDropdown="hide"
        >
          <template #moreOptions="{ state: moreOptionsState, hide: moreOptionsHide, member }">
            <ListDropdown
              v-if="moreOptionsState.isVisible"
              :items="moreOptionItems"
              :prevent-blur="true"
              @click="(item) => handleMoreOptionClick(item, member, moreOptionsHide)"
              @closeEscape="moreOptionsHide"
            />
          </template>
        </MembersDropdownList>
      </template>
    </v-popover>
    <v-popover
      v-if="showMetadata.date"
      ref="datePopover"
      interactive
      tag="div"
      content-tag="div"
      :trigger="'click'"
      :role="'poper'"
      :placement="'bottom-start'"
    >
      <ButtonCustom
        v-tooltip="{
          content: translate('editMeetingDate'),
          placement: 'bottom',
        }"
        :text="dateMetadataLabel"
        type="secondary-regular secondary-button electron-no-drag metadata-button"
        :custom-icon-name="'calendar'"
        :custom-icon-color="'--var(iconDefault)'"
        :custom-icon-size="'16px'"
      />
      <template #content="{ state, hide }">
        <DateTimePicker
          v-if="state.isVisible"
          :selected-date="selectedDate"
          @dueDate="(date) => setMetadataDate(date, hide)"
          @esc="hide"
        />
      </template>
    </v-popover>
    <v-popover
      v-if="showMetadata.time"
      ref="timePopover"
      interactive
      tag="div"
      content-tag="div"
      :trigger="'click'"
      :role="'poper'"
      :placement="'bottom-start'"
      :on-hide="handleUpdateTime"
      :on-show="focusTimeInput"
    >
      <ButtonCustom
        v-tooltip="{
          content: translate('editMeetingTime'),
          placement: 'bottom',
        }"
        :text="timeMetadataLabel"
        type="secondary-regular secondary-button electron-no-drag metadata-button"
        :custom-icon-name="'clock'"
        :custom-icon-color="'--var(iconDefault)'"
        :custom-icon-size="'16px'"
      />
      <template #content="{ state, hide }">
        <div
          v-if="state.isVisible"
          class="flex p-2 gap-2 items-center border bg-standard-bg border-solid border-standard-border rounded"
        >
          <input
            ref="timeInputRef"
            v-model="timeInput"
            type="time"
            class="time-input"
            @keydown="(e) => handleTimeKeydown(e, hide)"
            @change="handleTimeChange"
          />
          <em
            class="custom-icons pt delete-icon"
            :style="[getCustomIconStyle(iconPath('trash'))]"
            @click="setMetadataTime(null, null, hide)"
          />
        </div>
      </template>
    </v-popover>
    <v-popover
      v-if="!hasAllMetadata"
      interactive
      tag="div"
      content-tag="div"
      :trigger="'click'"
      :role="'poper'"
      :placement="'bottom-start'"
      :on-hide="() => (metadataQuery = '')"
    >
      <ButtonCustom
        v-tooltip="{
          content: translate('addMeetingDetails'),
          placement: 'bottom',
          onShow: () => hasNoMetadata,
        }"
        type="secondary-regular secondary-button electron-no-drag add-metadata-button pt"
        :custom-icon-name="'plus'"
        :custom-icon-color="'--var(iconDefault)'"
        :custom-icon-size="'16px'"
      />
      <template #content="{ state, hide }">
        <FilterDropdown
          v-if="state.isVisible"
          :search-results="metadataDropdownItems"
          :placeholder="`${translate('meetingDetails')}...`"
          :on-item-click="(item) => handleMetadataDropdownItemClick(item, hide)"
          :on-item-right-side-click="(item) => handleMetadataDropdownItemClick(item, hide)"
          :select-only-search-result="(item) => handleMetadataDropdownItemClick(item, hide)"
          :hide-filter-popup="hide"
          @query-change="handleMetadataQueryChange"
          @esc="hide"
        />
      </template>
    </v-popover>
  </div>
</template>
<script setup lang="ts">
import { computed, nextTick, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { TeamMemberStatus } from '@superthread-com/api';
import constants from '@/utilities/constants';
import CurrentModelMembers from '@/components/widgets/CurrentModelMembers';
import MembersDropdownList from '@/components/widgets/MembersDropdownList';
import DateTimePicker from '@/components/widgets/DateTimePicker';
import ButtonCustom from '@/components/elements/ButtonCustom';
import FilterDropdown from '@/components/widgets/filters/FilterDropdown';
import useNotes from '@/utilities/composables/useNotes';
import useTranslate from '@/utilities/composables/useTranslate';
import { convertTimestampToDateSecondFormat } from '@/utilities/timeDifference';
import ListDropdown from '@/components/widgets/ListDropdown';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import freeEmailDomains from 'free-email-domains';
import capitalizeFirstLetter from '@/utilities/capitalizeFirstLetter';

const store = useStore();
const { getNoteMetadata, updateNoteMetadataAttendees, updateNote, getNoteMeetingStartTime } =
  useNotes();
const { translate } = useTranslate();
const { iconPath, getCustomIconStyle } = useCustomIcon();

const metadataQuery = ref('');
const oldAttendees = ref([]);
const oldMetadataTime = ref(null);
const timeInput = ref('');
const timeInputRef = ref();
const attendeesPopover = ref();
const datePopover = ref();
const timePopover = ref();
const attendeesDropdown = ref();

const moreOptionItems = [
  {
    id: 'copyEmail',
    label: translate('copyEmail'),
    customIconName: iconPath('mail'),
  },
  {
    id: 'removeAttendee',
    label: translate('removeAttendee'),
    customIconName: iconPath('trash'),
    deleteOrCancelType: true,
  },
];

const metadataPopovers = {
  attendees: attendeesPopover,
  date: datePopover,
  time: timePopover,
};

const showMetadataSection = ref({
  date: false,
  time: false,
  attendees: false,
});

const props = defineProps({
  noteId: {
    type: String,
    required: true,
  },
});

const noteMetadata = computed(() => getNoteMetadata(props.noteId) || {});

const metadataTime = ref(noteMetadata.value.meetingStartTime);

const attendees = computed(() => noteMetadata.value.meetingAttendees);

const teamMembers = computed(() => {
  return store.getters.getTeamMembers || {};
});

const membersNotInTeam = computed(() => {
  return attendees.value.filter((m) => !m.user_id).map((m) => ({ ...m, id: m.user_id }));
});

const members = computed(() => {
  const existingMembers = Object.values(teamMembers.value).filter(
    (m) => m.status !== TeamMemberStatus.Invited
  );

  /**
   * Members with personal emails are shown first, then members which are
   * grouped by company domain, and existing workspace members are shown last.
   */

  const emailGroups = {
    personal: [],
    company: [],
  };

  membersNotInTeam.value.forEach((m) => {
    if (freeEmailDomains.includes(m.email.split('@')[1]))
      emailGroups.personal.push({ ...m, isPersonalEmail: true });
    else {
      const emailDomain = m.email.split('@')[1];
      const emailGroup = capitalizeFirstLetter(emailDomain.split('.')[0]);
      emailGroups.company.push({ ...m, emailGroup });
    }
  });

  return [...emailGroups.personal, ...emailGroups.company, ...existingMembers]
    .map((m) => ({
      ...m,
      checked: !m.id || attendees.value.some((a) => a.user_id === m.id),
      ...(!m.isPersonalEmail && {
        groupingItem: true,
        groupLabel: m.emailGroup || translate('billing.category.workspaceMembers'),
      }),
    }))
    .sort((a, b) => b.checked - a.checked);
});

const showMetadata = computed(() => {
  return {
    attendees: attendees.value.length > 0 || showMetadataSection.value.attendees,
    date: noteMetadata.value.meetingStartDate || showMetadataSection.value.date,
    time: noteMetadata.value.meetingStartTime || showMetadataSection.value.time,
  };
});

const hasAllMetadata = computed(() => {
  const { attendees, date, time } = showMetadata.value;
  return attendees && date && time;
});

const hasNoMetadata = computed(() => {
  const { attendees, date, time } = showMetadata.value;
  return !attendees && !date && !time;
});

const metadataDropdownItems = computed(() => {
  return [
    ...(!noteMetadata.value.meetingStartDate && !showMetadataSection.value.meetingStartDate
      ? [
          {
            id: 'date',
            label: translate('date'),
            resourceIcon: { src: 'calendar' },
          },
        ]
      : []),
    ...(!noteMetadata.value.meetingStartTime && !showMetadataSection.value.meetingStartTime
      ? [
          {
            id: 'time',
            label: translate('time'),
            resourceIcon: { src: 'clock' },
          },
        ]
      : []),
    ...(attendees.value.length === 0 && !showMetadataSection.value.attendees
      ? [
          {
            id: 'attendees',
            label: translate('attendees'),
            resourceIcon: { src: 'users' },
          },
        ]
      : []),
  ].filter((item) => item.label.toLowerCase().includes(metadataQuery.value.toLowerCase()));
});

const dateMetadataLabel = computed(() =>
  convertTimestampToDateSecondFormat(noteMetadata.value.meetingStartDate)
);

const selectedDate = computed(() => {
  return noteMetadata.value.meetingStartDate || new Date() / 1000;
});

const timeMetadataLabel = computed(() => getNoteMeetingStartTime(props.noteId));

const handleAttendeeClick = (attendee: any) => {
  updateNoteMetadataAttendees({
    noteId: props.noteId,
    attendee,
    removeAttendee: attendees.value.some((a) => a.user_id === attendee.id),
    local: true,
  });

  focusAttendeesDropdown();
};

const handleUpdateAttendees = () => {
  updateNoteMetadataAttendees({ noteId: props.noteId, oldAttendees: oldAttendees.value });
  oldAttendees.value = attendees.value;
};

const openMetadataDropdown = (id: string) => {
  showMetadataSection.value[id] = true;

  requestAnimationFrame(() => {
    nextTick(() => {
      metadataPopovers[id].value?.show();
    });
  });
};

const setMetadataDate = (date: Date, hide?: () => void) => {
  updateNote({
    noteId: props.noteId,
    localField: 'meeting_date',
    fieldToUpdate: 'metadata_date',
    value: date,
  });

  // Hide the date from the shown sections if it has been removed
  if (!date) showMetadataSection.value.date = false;

  hide?.();
};

const setMetadataTime = (hours: number, minutes: number, hide?: () => void) => {
  if (hours === null && minutes === null) {
    metadataTime.value = null;
    showMetadataSection.value.time = false;

    hide?.();
    return;
  }

  const d = new Date().setHours(hours, minutes, 0);
  metadataTime.value = d / 1000;
};

const focusTimeInput = () => {
  nextTick(() => {
    timeInputRef.value?.focus();
  });
};

const handleTimeKeydown = (e: any, hide: () => void) => {
  if (['Escape', 'Enter'].includes(e.key)) hide();
};

const handleTimeChange = (e: any) => {
  const v = e.target.value;
  const [hours, minutes] = v.split(':');
  setMetadataTime(hours, minutes);
};

const handleUpdateTime = () => {
  if (oldMetadataTime.value === metadataTime.value) return;

  updateNote({
    noteId: props.noteId,
    localField: 'meeting_time',
    fieldToUpdate: 'metadata_time',
    value: Math.round(metadataTime.value),
  });
  oldMetadataTime.value = metadataTime.value;
};

const handleMetadataDropdownItemClick = (item: any, hide: () => void) => {
  openMetadataDropdown(item.id);

  const currentDate = new Date();
  const currentDateInSeconds = Math.round(new Date() / 1000);

  // Immediately set the current date or time once clicked
  if (item.id === 'date') setMetadataDate(currentDateInSeconds);
  if (item.id === 'time') setMetadataTime(currentDate.getHours(), currentDate.getMinutes());

  hide();
};

const handleMetadataQueryChange = (query: string) => {
  metadataQuery.value = query;
};

const handleMoreOptionClick = (item: any, member: any, hide?: () => void) => {
  if (item.id === 'copyEmail') {
    navigator.clipboard.writeText(member.email);
    store.dispatch('addUiNotification', {
      message: translate('emailCopiedToClipboard'),
      icon: 'mail',
      duration: 4000,
    });
  }

  if (item.id === 'removeAttendee') handleAttendeeClick(member);

  hide();
};

const handleAttendeesClickOutside = (i: any, e: any) => {
  // Prevent closing if nested dropdown items are clicked
  if (e.target.closest('.list-dropdown-scroll')) return;
  i.hide();
};

const focusAttendeesDropdown = () => {
  nextTick(() => {
    attendeesDropdown.value?.$refs?.searchInput?.focus();
  });
};

const handleClickAttendeesPopover = () => {
  const popover = attendeesPopover.value;
  if (popover?.state?.isVisible) popover?.hide();
  else popover?.show();
};

onMounted(() => {
  oldAttendees.value = attendees.value;
  oldMetadataTime.value = noteMetadata.value.meetingStartTime;
  timeInput.value = timeMetadataLabel.value;
});
</script>
<style lang="scss">
.note-metadata-wrapper {
  @apply flex items-center gap-2;

  .list-wrap {
    @apply w-[300px];
  }

  .add-members-wrapper {
    @apply mr-0;

    .add-members-button {
      @apply w-5 h-5 box-border m-0 p-[1px];
    }

    .members {
      @apply -mr-1;
    }

    .avatars-draggable {
      @apply box-border h-5 -ml-0.5 mr-0.5;

      .member-avatar {
        @apply ml-0;
      }

      .member-avatar:not(:first-child) {
        @apply -ml-1;
      }

      .user-avatar-main {
        width: 20px !important;
        height: 20px !important;

        .avatar-initials {
          @apply w-5 h-5;
        }
      }

      .rest-member-wrap {
        @apply -mr-0.5;
      }
    }
  }

  .metadata-button {
    @apply p-1 h-5 flex-shrink-0;
  }

  .add-metadata-button {
    @apply rounded-full w-5 h-5 p-[1px] hover:bg-hover-default;

    .button-label {
      @apply hidden;
    }

    .button-icon {
      @apply mr-0;
    }
  }

  .time-input {
    @apply h-9 text-center text-base text-text-default px-3 rounded
      border-standard-border border-solid border bg-standard-bg focus:outline-none;
  }

  input[type='time']::-webkit-calendar-picker-indicator {
    @apply hidden;
  }
}
</style>
